import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { RoutesLink } from '@/utility/RouteLinks';
import { ThrowError } from '@/api/callApi';
import type { INotification } from '@/interface/common.interface';
import { NotificationContext } from '@/Context/NotificationContext';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { RootState } from '@/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { setNotificationCount } from '@/store/feature/UserSlice';
import { NotificationService } from '@/services/Notification/NotificationService';
import { Button } from '../ui/button';
import { showToastNotification } from '@/utility/helper';

const PAGE_SIZE = 10;

const NotificationDropdown: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { setIsShowNotification } = useContext(NotificationContext);
    const currentUser = useSelector((state: RootState) => state.user.user);
    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const scrollRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    // const unreadCount = notifications.filter(n => !n.seen).length;
    const unreadCount = useSelector((state: RootState) => state.user.notificationCount);
    // console.log("unreadCount", unreadCount)






    // ── Fetch one page of unseen notifications ──────────────────────────────
    // const fetchPage = useCallback(async (pageNumber: number, reset = false) => {
    //     if (isFetching) return;
    //     setIsFetching(true);
    //     try {
    //         const response: any = await callApi(
    //             `${API_ENDPOINT.notification}?seen=false&pageNumber=${pageNumber}&pageSize=${PAGE_SIZE}`,
    //             { method: 'GET' }
    //         );
    //         const list: INotification[] = response?.data?.notifications ?? [];
    //         const totalPages: number = response?.data?.pageInfo?.totalPages ?? 1;

    //         setNotifications(prev => reset ? list : [...prev, ...list]);
    //         setHasMore(pageNumber < totalPages);
    //     } catch {
    //         // silent fail
    //     } finally {
    //         setIsFetching(false);
    //     }
    // }, [isFetching]);

    const fetchPage = useCallback(
        async (pageNumber: number, reset = false) => {
            if (isFetching) return;
            setIsFetching(true);
            try {
                const response = await NotificationService.getList({
                    pageNumber,
                    pageSize: PAGE_SIZE,
                    seen: false,
                });
                const list = response?.data?.notifications ?? [];
                const totalPages = response?.data?.pageInfo?.totalPages ?? 1;

                setNotifications((prev) => (reset ? list : [...prev, ...list]));
                setHasMore(pageNumber < totalPages);
            } catch (error) {
                ThrowError(error);
            } finally {
                setIsFetching(false);
            }
        },
        [isFetching]
    );


    useEffect(() => {
        if (isOpen) {
            setPage(1);
            setHasMore(true);
            fetchPage(1, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, unreadCount]);

    // useEffect(() => {
    //     const POLL_INTERVAL = 30000;

    //     const intervalId = setInterval(() => {
    //         dispatch(fetchNotificationCount() as any);
    //     }, POLL_INTERVAL);


    //     return () => clearInterval(intervalId);
    // }, [dispatch]);


    // ── Infinite scroll: observe sentinel element ────────────────────────────
    useEffect(() => {
        if (!isOpen) return;

        observerRef.current?.disconnect();

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isFetching) {
                    const next = page + 1;
                    setPage(next);
                    fetchPage(next);
                }
            },
            { root: scrollRef.current, threshold: 0.5 }
        );

        if (sentinelRef.current) {
            observerRef.current.observe(sentinelRef.current);
        }

        return () => observerRef.current?.disconnect();
    }, [isOpen, hasMore, isFetching, page, fetchPage]);

    // ── Mark single notification as seen (local) ─────────────────────────────
    // const markSeen = (id: string) => {
    //     console.log('markSeen', id);

    //     setNotifications(prev => prev.map(n => n.id === id ? { ...n, seen: true } : n));
    // };


    const markSeen = async (id: string) => {
        try {
            await NotificationService.markAsSeen([id], true);
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, seen: true } : n))
            );
            dispatch(setNotificationCount(Math.max(unreadCount - 1, 0)));
        } catch (error) {
            ThrowError(error);
        }
    };

    // ── Mark all as seen (local) ─────────────────────────────────────────────
    // const markAllSeen = () => {
    //     setNotifications(prev => prev.map(n => ({ ...n, seen: true })));
    // };

    const markAllSeen = async () => {
        const unseenIds = notifications.filter((n) => !n.seen).map((n) => n.id);
        if (unseenIds.length === 0) return;

        try {
            await NotificationService.markAsSeen(unseenIds, true);
            setNotifications((prev) => prev.map((n) => ({ ...n, seen: true })));
            dispatch(setNotificationCount(0));
        } catch (error) {
            ThrowError(error);
        }
    };

    const handleNotificationClick = (notification: INotification) => {
        // If unread, mark as seen
        if (!notification.seen) {
            markSeen(notification.id);
        }
        setIsOpen(false);
        setIsShowNotification(false);

        const rawType = notification?.data?.type || "";
        const type = String(rawType).toLowerCase().trim();
        const data = notification?.data;
        // const userId = data.userId

        if (!data) return;

        const userLevel = currentUser?.designation?.level;
        const isLevelAdmin =
            userLevel !== undefined &&
            userLevel !== null &&
            (Number(userLevel) === 0 || Number(userLevel) === 1);
        const isAdmin =
            currentUser?.type === "admin" || currentUser?.type === "super_admin";
        const canAccessTeam =
            Boolean(currentUser?.designation?.canAccessTeam) ||
            isLevelAdmin ||
            isAdmin;

        // Extract relevant ID based on type or fallbacks
        const targetId =
            data.leaveId ||
            data.wfhId ||
            data.outdoorDutyId ||
            (data as any).regularizationId ||
            (data as any).id ||
            "";
        const Id = data.userId ? data?.userId : targetId;
        if (!targetId) return;

        if (type === "leave") {
            if (canAccessTeam) {
                navigate(`${RoutesLink.teamLeaveDetail}/${targetId}`);
            } else if (!data?.userId) {
                navigate(`${RoutesLink.leaveDetail}/${Id}`);
            } else {
                showToastNotification(
                    "danger",
                    "You do not have permission to access team leave requests."
                );
            }
        } else if (type === "wfh") {
            if (canAccessTeam) {
                navigate(`${RoutesLink.teamWfhDetail}/${targetId}`);
            } else if (!data?.userId) {
                navigate(`${RoutesLink.wfhDetail}/${Id}`);
            } else {
                showToastNotification(
                    "danger",
                    "You do not have permission to access team WFH requests."
                );
            }
        } else if (type === "outdoor-duty" || type === "outdoor_duty" || type === "od") {
            if (canAccessTeam) {
                navigate(`${RoutesLink.teamOdDetail}/${targetId}`);
            } else if (!data?.userId) {
                navigate(`${RoutesLink.odDetail}/${Id}`);
            } else {
                showToastNotification(
                    "danger",
                    "You do not have permission to access team OD requests."
                );
            }
        } else if (type === "regularization") {
            if (canAccessTeam) {
                navigate(`${RoutesLink.teamsRegularizationDetail}/${targetId}`);
            } else if (!data?.userId) {
                navigate(`${RoutesLink.regularizationDetail}/${Id}`);
            } else {
                showToastNotification(
                    "danger",
                    "You do not have permission to access team regularization requests."
                );
            }
        } else if (data.userId) {
            navigate(`${RoutesLink.employeeDetail}/${data.userId}`);
        }
    };

    const formatDate = (date: string | Date) =>
        new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });

    return (
        <DropdownMenu
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                setIsShowNotification(open);
            }}
        >
            {/* ── Bell trigger ── */}
            <DropdownMenuTrigger asChild>
                <Button variant={"outline"} size='icon' className='rounded-full sm:h-10 sm:w-10 h-9 w-9 relative'>
                    <Bell size={19} className="group-hover:text-primary transition-colors" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center border-2 border-white shadow-sm animate-pulse">
                            {unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>

            {/* ── Panel ── */}
            <DropdownMenuContent
                align="end"
                className="w-80 sm:w-96 bg-white p-0 overflow-hidden shadow-xl border border-gray-100 rounded-xl"
            >
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="font-semibold text-sm text-gray-800">Notifications</span>
                    {unreadCount > 0 && (
                        <button
                            onClick={markAllSeen}
                            className="text-xs text-primary hover:text-primary/80 font-semibold transition-colors cursor-pointer"
                        >
                            Mark all as read
                        </button>
                    )}
                </div>

                {/* Scrollable list with infinite scroll */}
                <div ref={scrollRef} className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 && !isFetching ? (
                        <div className="flex flex-col items-center justify-center py-10 px-4">
                            <Bell size={32} className="text-gray-300 mb-2" />
                            <p className="text-xs text-gray-400 font-medium">No new notifications</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-50">
                            {notifications?.map((notification) => (
                                <DropdownMenuItem
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    className="flex items-start gap-3 p-3.5 hover:bg-slate-50/50 cursor-pointer focus:bg-slate-50 transition-colors"
                                >
                                    {/* Icon */}
                                    <div className="h-8 w-8 rounded-full bg-linear-to-tr from-primary/10 to-primary/5 text-primary flex items-center justify-center shrink-0">
                                        <Bell size={14} />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        {notification?.title && (
                                            <p className="text-xs font-semibold text-gray-800 leading-tight mb-0.5 truncate">
                                                {notification?.title}
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-500 leading-relaxed break-words">
                                            {notification?.message}
                                        </p>
                                        <span className="text-[10px] text-gray-400 font-medium mt-1 block">
                                            {notification?.createdAt ? formatDate(notification?.createdAt) : ''}
                                        </span>
                                    </div>

                                    {/* Unread dot */}
                                    {!notification?.seen && (
                                        <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
                                    )}
                                </DropdownMenuItem>
                            ))}

                            {/* Sentinel — triggers next page load */}
                            <div ref={sentinelRef} className="h-1" />

                            {/* Loading indicator */}
                            {isFetching && (
                                <div className="flex justify-center py-3">
                                    <span className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}

                            {/* End of list */}
                            {!hasMore && notifications?.length > 0 && (
                                <p className="text-center text-[10px] text-gray-400 py-3">
                                    No more notifications
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 p-2 bg-gray-50/50">
                    <button
                        onClick={() => {
                            setIsOpen(false);
                            navigate(RoutesLink.notification);
                        }}
                        className="w-full py-2 text-center text-xs font-semibold text-primary hover:text-primary/80 bg-white border border-gray-200 rounded-lg hover:border-primary/20 shadow-sm transition-all cursor-pointer block"
                    >
                        View All
                    </button>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default NotificationDropdown;
